import React, { useState, useMemo } from "react";
import { useUserGroups } from "../../hooks/useUserGroups";
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  Badge,
  Tooltip,
} from "@mui/material";
import AssignFormDialog from "./AssignFormDialog";
import { useAuthenticatedQuery } from "../../hooks/useAuthenticatedQuery";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import AssignmentInd from "@mui/icons-material/AssignmentInd";
import PeopleIcon from "@mui/icons-material/People";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import FolderIcon from "@mui/icons-material/Folder";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import { LIST_FORMS, GET_ALL_NOTIFICATIONS } from "../../graphql/operations";
import { Form, FormStatus, FormSection } from "../../graphql/operations"; // Import Form and FormSection
import { Helmet } from "react-helmet-async";

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  formId: string;
  formName: string;
  username: string;
  submissionId: string | null;
  approvedBy: string | null;
  dueDate: string;
  isRead: boolean;
  createdAt: string;
}

const FormList: React.FC = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: groupsLoading } = useUserGroups();
  const [filters, setFilters] = useState({
    title: "",
    formType: "",
  });
  const [activeTab, setActiveTab] = useState(0);

  const {
    data: formsData,
    loading: formsLoading,
    error: formsError,
  } = useAuthenticatedQuery(LIST_FORMS, {
    skip: !isAdmin,
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables: {
      filter: {
        title: filters.title || undefined,
        formType: filters.formType || undefined,
      },
    },
  });

  const { data: notificationsData, loading: notificationsLoading } =
    useAuthenticatedQuery(GET_ALL_NOTIFICATIONS, {
      skip: !isAdmin,
    });

  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const handleAssignClick = (form: Form) => {
    setSelectedForm({ id: form.id, title: form.title });
    setAssignDialogOpen(true);
  };

  const handleViewUsersClick = (form: Form) => {
    setSelectedForm({ id: form.id, title: form.title });
    setUsersDialogOpen(true);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Process notifications to get user status per form
  const formUserStatus = useMemo(() => {
    if (!notificationsData?.getAllNotifications?.items) return {};

    const notifications: Notification[] =
      notificationsData.getAllNotifications.items;
    const statusMap: {
      [formId: string]: {
        assigned: Notification[];
        submitted: Notification[];
        approved: Notification[];
      };
    } = {};

    notifications.forEach((notification) => {
      if (!statusMap[notification.formId]) {
        statusMap[notification.formId] = {
          assigned: [],
          submitted: [],
          approved: [],
        };
      }

      // Categorize based on submissionId and approvedBy
      if (notification.submissionId === null) {
        // Not submitted yet (assigned but pending)
        statusMap[notification.formId].assigned.push(notification);
      } else if (notification.approvedBy === null) {
        // Submitted but not approved
        statusMap[notification.formId].submitted.push(notification);
      } else {
        // Submitted and approved
        statusMap[notification.formId].approved.push(notification);
      }
    });

    return statusMap;
  }, [notificationsData]);

  if (!groupsLoading && !isAdmin) {
    return (
      <Box>
        <Helmet>
          <title>Almond Tree Dashboard - Access Denied</title>
        </Helmet>
        <Alert severity="error" sx={{ mt: 2 }}>
          You do not have permission to view forms. Please contact an
          administrator.
        </Alert>
      </Box>
    );
  }

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      title: "",
      formType: "",
    });
  };

  const hasActiveFilters = filters.title || filters.formType;

  const getStatusColor = (status: FormStatus) => {
    switch (status) {
      case FormStatus.ACTIVE:
        return "success";
      case FormStatus.DRAFT:
        return "warning";
      case FormStatus.ARCHIVED:
        return "default";
      default:
        return "default";
    }
  };

  // Calculate form statistics - NEW VERSION
  const calculateFormStats = (form: Form) => {
    // Form now always has sections (might be empty array)
    const sections = form.sections || [];
    const totalFields = sections.reduce(
      (sum: number, section: FormSection) =>
        sum + (section.fields?.length || 0),
      0
    );
    const totalSections = sections.length;

    return {
      totalFields,
      totalSections,
      hasSections: totalSections > 0,
    };
  };

  // Helper to get sections or create default section for old forms
  // const getFormSections = (form: Form): FormSection[] => {
  //   if (form.sections && form.sections.length > 0) {
  //     return form.sections;
  //   }

  //   // For backward compatibility with old forms that have fields directly
  //   const fields = (form as any).fields || [];
  //   if (fields.length > 0) {
  //     return [
  //       {
  //         id: "default-section",
  //         title: "Form Fields",
  //         subtitle: "",
  //         order: 0,
  //         fields: fields,
  //       },
  //     ];
  //   }

  //   return [];
  // };

  // Helper to get form type label
  const getFormTypeLabel = (formType: string): string => {
    const formTypes = {
      EMPLOYEE: "Employee",
      LOCATION: "Location",
      COMPLIANCE: "Compliance",
    };
    return formTypes[formType as keyof typeof formTypes] || formType;
  };

  if (formsLoading || notificationsLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (formsError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Error loading forms: {formsError.message}
      </Alert>
    );
  }

  const forms: Form[] = formsData?.listForms?.items || [];

  return (
    <Box>
      <Helmet>
        <title>Almond Tree Dashboard - Form List</title>
      </Helmet>

      {/* AdminHeader */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Forms</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/dashboard/forms/new")}
          sx={{ backgroundColor: "#919749" }}
        >
          Create Form
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FilterListIcon color="action" sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
          {hasActiveFilters && (
            <Button
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              sx={{ ml: 2 }}
              size="small"
            >
              Clear Filters
            </Button>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Form Type</InputLabel>
            <Select
              value={filters.formType}
              label="Form Type"
              onChange={(e) => handleFilterChange("formType", e.target.value)}
            >
              <MenuItem value="">All Types</MenuItem>
              <MenuItem value="EMPLOYEE">Employee</MenuItem>
              <MenuItem value="LOCATION">Location</MenuItem>
              <MenuItem value="COMPLIANCE">Compliance</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Results Count */}
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {forms.length} form(s) found
        {hasActiveFilters && " (filtered)"}
      </Typography>

      {/* Forms List */}
      <Paper>
        {forms.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {hasActiveFilters
                ? "No forms match your filters"
                : "No forms found"}
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              {hasActiveFilters
                ? "Try adjusting your filters or clear them to see all forms."
                : "Get started by creating your first form."}
            </Typography>
            {hasActiveFilters ? (
              <Button variant="outlined" onClick={clearFilters}>
                Clear Filters
              </Button>
            ) : (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => navigate("/dashboard/forms/new")}
                sx={{ backgroundColor: "#919749" }}
              >
                Create First Form
              </Button>
            )}
          </Box>
        ) : (
          forms.map((form: Form) => {
            const formStatus = formUserStatus[form.id] || {
              assigned: [],
              submitted: [],
              approved: [],
            };
            const totalAssigned =
              formStatus.assigned.length +
              formStatus.submitted.length +
              formStatus.approved.length;

            const stats = calculateFormStats(form);
            const sections = form.sections || [];

            return (
              <Box
                key={form.id}
                sx={{
                  p: 2,
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:last-child": { borderBottom: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "start",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="h6" sx={{ mr: 2 }}>
                        {form.title}
                      </Typography>
                      <Chip
                        label={form.status}
                        size="small"
                        color={getStatusColor(form.status)}
                        variant="outlined"
                      />
                    </Box>

                    {form.description && (
                      <Typography color="textSecondary" paragraph>
                        {form.description}
                      </Typography>
                    )}

                    <Stack
                      direction="row"
                      spacing={1}
                      sx={{ mb: 1, flexWrap: "wrap", gap: 1 }}
                    >
                      <Chip
                        label={`v${form.version}`}
                        size="small"
                        variant="outlined"
                      />

                      {/* Section and Field Statistics */}
                      {stats.hasSections ? (
                        <>
                          <Tooltip title="Number of sections">
                            <Chip
                              icon={<FolderIcon />}
                              label={`${stats.totalSections} sections`}
                              size="small"
                              variant="outlined"
                              color="primary"
                            />
                          </Tooltip>
                          <Tooltip title="Total fields across all sections">
                            <Chip
                              icon={<TextFieldsIcon />}
                              label={`${stats.totalFields} fields`}
                              size="small"
                              variant="outlined"
                              color="secondary"
                            />
                          </Tooltip>
                        </>
                      ) : (
                        <Chip
                          label={`${stats.totalFields} fields`}
                          size="small"
                          variant="outlined"
                          color="secondary"
                        />
                      )}

                      <Chip
                        label={getFormTypeLabel(form.formType)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />

                      {/* Assignment Status Chips */}
                      {totalAssigned > 0 && (
                        <>
                          <Tooltip title="Total users assigned to this form">
                            <Chip
                              label={`${totalAssigned} assigned`}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          </Tooltip>
                          {formStatus.submitted.length > 0 && (
                            <Tooltip title="Submissions pending approval">
                              <Chip
                                label={`${formStatus.submitted.length} pending`}
                                size="small"
                                variant="outlined"
                                color="warning"
                              />
                            </Tooltip>
                          )}
                          {formStatus.assigned.length > 0 && (
                            <Tooltip title="Assignments not yet submitted">
                              <Chip
                                label={`${formStatus.assigned.length} not submitted`}
                                size="small"
                                variant="outlined"
                                color="error"
                              />
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Stack>

                    {/* Sections Preview (if form has sections) */}
                    {stats.hasSections && sections.length > 0 && (
                      <Box sx={{ mt: 1, mb: 1 }}>
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          gutterBottom
                        >
                          Sections:
                        </Typography>
                        <Stack
                          direction="row"
                          spacing={0.5}
                          sx={{ flexWrap: "wrap", gap: 0.5 }}
                        >
                          {sections.slice(0, 3).map((section, idx) => (
                            <Chip
                              key={section.id || idx}
                              label={`${section.title} (${
                                section.fields?.length || 0
                              })`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          ))}
                          {sections.length > 3 && (
                            <Chip
                              label={`+${sections.length - 3} more`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                        </Stack>
                      </Box>
                    )}

                    <Typography variant="caption" color="textSecondary">
                      Created: {new Date(form.createdAt).toLocaleDateString()}
                      {form.updatedAt !== form.createdAt &&
                        ` • Updated: ${new Date(
                          form.updatedAt
                        ).toLocaleDateString()}`}
                    </Typography>
                  </Box>

                  <Box>
                    <Tooltip title="View assigned users">
                      <span>
                        <IconButton
                          onClick={() => handleViewUsersClick(form)}
                          color="info"
                          disabled={totalAssigned === 0}
                        >
                          <Badge
                            badgeContent={totalAssigned}
                            color="info"
                            max={99}
                          >
                            <PeopleIcon />
                          </Badge>
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Assign to employees">
                      <IconButton
                        onClick={() => handleAssignClick(form)}
                        color="primary"
                      >
                        <AssignmentInd />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title="Edit Form">
                      <IconButton
                        onClick={() =>
                          navigate(`/dashboard/forms/edit/${form.id}`)
                        }
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            );
          })
        )}
      </Paper>

      {/* Assign Form Dialog */}
      {selectedForm && (
        <AssignFormDialog
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          formId={selectedForm.id}
          formTitle={selectedForm.title}
        />
      )}

      {/* View Users Dialog */}
      <Dialog
        open={usersDialogOpen}
        onClose={() => setUsersDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Assigned Users - {selectedForm?.title}</DialogTitle>
        <DialogContent>
          {selectedForm && (
            <>
              {/* Summary Stats */}
              <Box
                sx={{
                  mb: 2,
                  p: 2,
                  backgroundColor: "grey.50",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" gutterBottom>
                  Submission Summary:
                </Typography>
                <Stack direction="row" spacing={3} flexWrap="wrap">
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Total Assigned
                    </Typography>
                    <Typography variant="h6">
                      {formUserStatus[selectedForm.id]
                        ? formUserStatus[selectedForm.id].assigned.length +
                          formUserStatus[selectedForm.id].submitted.length +
                          formUserStatus[selectedForm.id].approved.length
                        : 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Approved
                    </Typography>
                    <Typography variant="h6" color="success.main">
                      {formUserStatus[selectedForm.id]?.approved.length || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Pending Approval
                    </Typography>
                    <Typography variant="h6" color="warning.main">
                      {formUserStatus[selectedForm.id]?.submitted.length || 0}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="textSecondary">
                      Not Submitted
                    </Typography>
                    <Typography variant="h6" color="error.main">
                      {formUserStatus[selectedForm.id]?.assigned.length || 0}
                    </Typography>
                  </Box>
                </Stack>
              </Box>

              <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
                <Tab
                  label={
                    <Badge
                      badgeContent={
                        formUserStatus[selectedForm.id]?.submitted.length || 0
                      }
                      color="warning"
                      max={99}
                    >
                      <Box sx={{ px: 1 }}>Pending Approval</Box>
                    </Badge>
                  }
                />
                <Tab
                  label={
                    <Badge
                      badgeContent={
                        formUserStatus[selectedForm.id]?.assigned.length || 0
                      }
                      color="error"
                      max={99}
                    >
                      <Box sx={{ px: 1 }}>Not Submitted</Box>
                    </Badge>
                  }
                />
                <Tab
                  label={
                    <Badge
                      badgeContent={
                        formUserStatus[selectedForm.id]?.approved.length || 0
                      }
                      color="success"
                      max={99}
                    >
                      <Box sx={{ px: 1 }}>Approved</Box>
                    </Badge>
                  }
                />
              </Tabs>

              {/* Pending Approval Tab */}
              {activeTab === 0 && (
                <List dense>
                  {formUserStatus[selectedForm.id]?.submitted.length > 0 ? (
                    formUserStatus[selectedForm.id].submitted.map(
                      (notification) => (
                        <ListItem key={notification.id}>
                          <ListItemIcon>
                            <HourglassEmptyIcon color="warning" />
                          </ListItemIcon>
                          <ListItemText
                            primary={notification.username}
                            secondary={`Submitted: ${new Date(
                              notification.createdAt
                            ).toLocaleDateString()} • Due: ${new Date(
                              notification.dueDate
                            ).toLocaleDateString()}`}
                          />
                        </ListItem>
                      )
                    )
                  ) : (
                    <ListItem>
                      <ListItemText primary="No submissions pending approval" />
                    </ListItem>
                  )}
                </List>
              )}

              {/* Not Submitted Tab */}
              {activeTab === 1 && (
                <List dense>
                  {formUserStatus[selectedForm.id]?.assigned.length > 0 ? (
                    formUserStatus[selectedForm.id].assigned.map(
                      (notification) => (
                        <ListItem key={notification.id}>
                          <ListItemIcon>
                            <PendingIcon color="error" />
                          </ListItemIcon>
                          <ListItemText
                            primary={notification.username}
                            secondary={`Assigned: ${new Date(
                              notification.createdAt
                            ).toLocaleDateString()} • Due: ${new Date(
                              notification.dueDate
                            ).toLocaleDateString()}`}
                          />
                        </ListItem>
                      )
                    )
                  ) : (
                    <ListItem>
                      <ListItemText primary="No pending assignments" />
                    </ListItem>
                  )}
                </List>
              )}

              {/* Approved Tab */}
              {activeTab === 2 && (
                <List dense>
                  {formUserStatus[selectedForm.id]?.approved.length > 0 ? (
                    formUserStatus[selectedForm.id].approved.map(
                      (notification) => (
                        <ListItem key={notification.id}>
                          <ListItemIcon>
                            <CheckCircleIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={notification.username}
                            secondary={`Approved by: ${
                              notification.approvedBy
                            } • ${new Date(
                              notification.createdAt
                            ).toLocaleDateString()}`}
                          />
                        </ListItem>
                      )
                    )
                  ) : (
                    <ListItem>
                      <ListItemText primary="No approved submissions" />
                    </ListItem>
                  )}
                </List>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormList;
